import {
  Activity,
  AlertTriangle,
  Bitcoin,
  ChartNoAxesCombined,
  Coins,
  Database,
  Gem,
  Landmark,
  LayoutGrid,
  RefreshCw,
  ShieldCheck,
  TrendingUp
} from "lucide-react";
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sparkline } from "../../components/Charts";
import {
  clearMonitoringClientCache,
  getMonitoringHistory,
  getMonitoringSummary,
  getProviderHealth,
  getRefreshRun,
  peekMonitoringHistory,
  peekMonitoringSummary,
  refreshMonitoring
} from "../../data/api";
import type { AssetClass, DailyBar, MonitoringMetric, MonitoringWindow, ProviderHealthRecord, RefreshRunRecord } from "../../types";
import { number, pct } from "../../utils/calculations";

const loadMonitoringChart = () => import("../../components/EChartsMonitoringLine");
const MonitoringHistoryChart = lazy(() => loadMonitoringChart().then((module) => ({ default: module.MonitoringHistoryChart })));

type MonitorTab = "overview" | "index" | "futures" | "fx" | "commodity" | "crypto" | "rate";

const tabs: Array<{ id: MonitorTab; label: string; icon: typeof LayoutGrid }> = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "index", label: "Indexes", icon: TrendingUp },
  { id: "futures", label: "Futures", icon: ChartNoAxesCombined },
  { id: "fx", label: "FX", icon: Coins },
  { id: "commodity", label: "Commodities", icon: Gem },
  { id: "crypto", label: "Crypto", icon: Bitcoin },
  { id: "rate", label: "Interest Rates", icon: Landmark }
];
const ranges: MonitoringWindow[] = ["6M", "1Y", "3Y", "5Y", "10Y"];

export default function Monitoring() {
  const [initial] = useState(readUrlState);
  const [tab, setTab] = useState<MonitorTab>(initial.tab);
  const [range, setRange] = useState<MonitoringWindow>(initial.range);
  const [universe, setUniverse] = useState<MonitoringMetric[]>(() => (
    peekMonitoringSummary({ window: initial.range, group: "all" }) ?? []
  ));
  const [selectedId, setSelectedId] = useState(initial.instrumentId);
  const [providerHealth, setProviderHealth] = useState<ProviderHealthRecord[]>([]);
  const [detailHistory, setDetailHistory] = useState<DailyBar[]>([]);
  const [loading, setLoading] = useState(() => !peekMonitoringSummary({ window: initial.range, group: "all" }));
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshStatus, setRefreshStatus] = useState("Manual refresh ready");
  const [refreshRun, setRefreshRun] = useState<RefreshRunRecord | null>(null);
  const requestId = useRef(0);
  const refreshPollId = useRef(0);

  const metrics = useMemo(() => selectMetricsForTab(universe, tab), [tab, universe]);
  const selectMetric = useCallback((instrumentId: string) => setSelectedId(instrumentId), []);
  const preloadRange = useCallback((candidate: MonitoringWindow) => {
    void getMonitoringSummary({ window: candidate, group: "all" });
  }, []);

  useEffect(() => {
    const currentRequest = ++requestId.current;
    const options = { window: range, group: "all" as const };
    const cached = peekMonitoringSummary(options);
    if (cached) {
      setUniverse(cached);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    setUniverse([]);
    void getMonitoringSummary(options)
      .then((data) => {
        if (currentRequest !== requestId.current) return;
        setUniverse(data);
      })
      .catch((reason) => {
        if (currentRequest === requestId.current) setError(reason instanceof Error ? reason.message : "Monitoring data could not be loaded.");
      })
      .finally(() => {
        if (currentRequest === requestId.current) setLoading(false);
      });
  }, [range]);

  useEffect(() => {
    const refreshInProgress = refreshRun?.status === "queued" || refreshRun?.status === "running";
    if (refreshInProgress || !universe.length || universe[0]?.window !== range) return;
    const timer = window.setTimeout(() => {
      void Promise.all(ranges
        .filter((candidate) => candidate !== range)
        .map((candidate) => getMonitoringSummary({ window: candidate, group: "all" })));
    }, 80);
    return () => window.clearTimeout(timer);
  }, [range, refreshRun?.status, universe]);

  useEffect(() => {
    setSelectedId((current) => metrics.some((item) => item.instrument.id === current)
      ? current
      : metrics[0]?.instrument.id ?? "");
  }, [metrics]);

  useEffect(() => {
    void getProviderHealth()
      .then((payload) => setProviderHealth(payload.health))
      .catch(() => setProviderHealth([]));
    return () => {
      refreshPollId.current += 1;
    };
  }, []);

  const selected = useMemo(
    () => metrics.find((metric) => metric.instrument.id === selectedId) ?? metrics[0],
    [metrics, selectedId]
  );

  useEffect(() => {
    writeUrlState(tab, range, selected?.instrument.id ?? "");
  }, [range, selected?.instrument.id, tab]);

  useEffect(() => {
    if (!selected) {
      setDetailHistory([]);
      setDetailLoading(false);
      return;
    }
    const cached = peekMonitoringHistory(selected.instrument.id, range);
    setDetailHistory(cached ?? selected.history);
    if (cached) {
      setDetailLoading(false);
      return;
    }
    let mounted = true;
    setDetailLoading(true);
    void getMonitoringHistory(selected.instrument.id, range)
      .then((history) => {
        if (mounted) setDetailHistory(history.length ? history : selected.history);
      })
      .finally(() => {
        if (mounted) setDetailLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [range, selected?.instrument.id, selected?.quote.updatedAt]);

  const {
    staleCount,
    flaggedCount,
    completeCoverageCount,
    successRate,
    latestRefresh,
    drawdownRanking,
    dailyMoves
  } = useMemo(() => {
    let stale = 0;
    let flagged = 0;
    let complete = 0;
    let latest = "";
    const withData: MonitoringMetric[] = [];
    for (const metric of metrics) {
      if (metric.quote.freshness === "stale" || metric.quote.freshness === "offline") stale += 1;
      if (metric.qualityFlags.length) flagged += 1;
      if (metric.coverage.coveragePct >= 95) complete += 1;
      if (metric.coverage.points > 0 && metric.quote.price > 0) withData.push(metric);
      const candidate = metric.quote.updatedAt ?? metric.quote.asOf;
      if (candidate > latest) latest = candidate;
    }
    return {
      staleCount: stale,
      flaggedCount: flagged,
      completeCoverageCount: complete,
      successRate: metrics.length ? Math.round(((metrics.length - stale) / metrics.length) * 100) : 0,
      latestRefresh: latest,
      drawdownRanking: [...withData].sort((a, b) => a.drawdownFromRangeHighPct - b.drawdownFromRangeHighPct).slice(0, 6),
      dailyMoves: [...withData].sort((a, b) => Math.abs(b.quote.changePct) - Math.abs(a.quote.changePct)).slice(0, 6)
    };
  }, [metrics]);
  const refreshActive = refreshRun?.status === "queued" || refreshRun?.status === "running";
  const refreshProgress = refreshRun?.total ? Math.min(100, refreshRun.completed / refreshRun.total * 100) : 0;

  const reloadVisibleMetrics = async () => {
    clearMonitoringClientCache();
    const data = await getMonitoringSummary({ window: range, group: "all" }, { force: true });
    setUniverse(data);
  };

  const refresh = async () => {
    if (refreshActive) return;
    const pollId = ++refreshPollId.current;
    setRefreshStatus("Starting full-universe refresh...");
    try {
      const result = await refreshMonitoring();
      if (!result.enqueued || !result.run) {
        setRefreshStatus("Refresh completed");
        await reloadVisibleMetrics();
        return;
      }

      setRefreshRun(result.run);
      setRefreshStatus(`Queued 0 of ${result.run.total} instruments`);
      for (let attempt = 0; attempt < 120 && pollId === refreshPollId.current; attempt += 1) {
        await wait(1500);
        if (pollId !== refreshPollId.current) return;
        const run = await getRefreshRun(result.run.id);
        setRefreshRun(run);
        setRefreshStatus(formatRefreshStatus(run));
        if (attempt % 4 === 3 || isTerminalRun(run)) await reloadVisibleMetrics();
        if (isTerminalRun(run)) {
          const health = await getProviderHealth().catch(() => null);
          if (health) setProviderHealth(health.health);
          return;
        }
      }
      if (pollId === refreshPollId.current) setRefreshStatus("Refresh is still running on the server. Cached data remains visible.");
    } catch (reason) {
      setRefreshStatus(reason instanceof Error ? reason.message : "Refresh failed");
    }
  };

  return (
    <section className="route-page monitoring-page">
      <div className="page-head monitoring-head">
        <div>
          <p className="eyebrow">Global Market Monitoring</p>
          <h2>Daily prices in range context</h2>
          <span>Indexes, futures, FX, commodities, crypto, and sovereign rates in consistent range context.</span>
        </div>
        <div className="head-actions">
          <div className="monitor-refresh-control" aria-live="polite">
            <button className="btn" onClick={() => void refresh()} disabled={refreshActive} aria-busy={refreshActive}>
              <RefreshCw className={refreshActive ? "is-spinning" : ""} />
              {refreshActive && refreshRun ? `${refreshRun.completed}/${refreshRun.total} Updating` : "Refresh Data"}
            </button>
            <span>{refreshStatus}</span>
            {refreshActive && <i><b style={{ width: `${refreshProgress}%` }} /></i>}
          </div>
          <span className={`status-pill ${staleCount ? "warn" : ""}`}>{loading ? "Loading" : `${successRate.toFixed(1)}% Feeds Current`}</span>
        </div>
      </div>

      <div className="monitor-command-bar">
        <div className="monitor-category-tabs" role="tablist" aria-label="Market category">
          {tabs.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} className={tab === item.id ? "is-active" : ""} onClick={() => setTab(item.id)} role="tab" aria-selected={tab === item.id}>
                <Icon /> <span>{item.label}</span>
              </button>
            );
          })}
        </div>
        <div className="monitor-range-control" role="group" aria-label="History range">
          {ranges.map((item) => (
            <button
              key={item}
              className={range === item ? "is-active" : ""}
              onClick={() => setRange(item)}
              onFocus={() => preloadRange(item)}
              onMouseEnter={() => preloadRange(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="monitor-strip">
        <MetricPill label="Last Stored Update" value={formatTimestamp(latestRefresh)} />
        <MetricPill label="Stale / Offline" value={`${staleCount} of ${metrics.length}`} tone={staleCount ? "warn" : "good"} />
        <MetricPill label="Visible Universe" value={`${metrics.length} instruments`} />
        <MetricPill label="Refresh Policy" value="Daily 22:30 UTC + manual" tone="good" />
      </div>

      <div className="monitor-layout">
        <main className="monitor-main">
          {error && <div className="monitor-error"><AlertTriangle /> {error}</div>}
          <div className="market-card-grid" aria-busy={loading}>
            {loading && !metrics.length
              ? Array.from({ length: tab === "overview" ? 20 : 8 }, (_, index) => <MarketCardSkeleton key={index} />)
              : metrics.map((metric) => (
                <MarketCard
                  key={metric.instrument.id}
                  metric={metric}
                  range={range}
                  selected={selected?.instrument.id === metric.instrument.id}
                  onSelect={selectMetric}
                />
              ))}
          </div>
        </main>

        <aside className="monitor-side">
          <Ranking title={`Largest ${range} Drawdowns`} data={drawdownRanking} value={(item) => formatDrawdown(item)} />
          <Ranking title={tab === "rate" ? "Largest Latest Moves" : "Largest Daily Moves"} data={dailyMoves} value={(item) => formatChange(item)} dailyMove />
          <div className="side-panel data-quality-panel">
            <div className="section-title"><Database size={14} /> Data Quality</div>
            <QualityRow label="Complete range coverage" value={`${completeCoverageCount}/${metrics.length}`} tone={completeCoverageCount < metrics.length ? "warn" : "good"} />
            <QualityRow label="Stale or offline feeds" value={`${staleCount}`} tone={staleCount ? "bad" : "good"} />
            <QualityRow label="Quality flags" value={`${metrics.reduce((sum, item) => sum + item.qualityFlags.length, 0)}`} tone={flaggedCount ? "warn" : "good"} />
            <QualityRow label="Provider checks" value={`${providerHealth.filter((item) => item.status === "success").length}/${providerHealth.length}`} />
          </div>
          <div className="side-panel provider-compact-panel">
            <div className="section-title"><ShieldCheck size={14} /> Provider Health</div>
            {providerHealth.slice(0, 5).map((provider) => (
              <div className="rank-row" key={provider.provider}>
                <span>{provider.provider}</span>
                <b>{provider.status}</b>
                <em className={provider.riskLevel === "high" ? "red" : provider.riskLevel === "medium" ? "amber" : "green"}>{provider.riskLevel}</em>
              </div>
            ))}
            {!providerHealth.length && <p className="notes">No provider checks have completed.</p>}
          </div>
        </aside>
      </div>

      {selected && (
        <div className="detail-panel monitoring-detail-panel">
          <div className="detail-head">
            <div>
              <p className="detail-kicker"><Activity /> Selected instrument</p>
              <h3>{selected.instrument.name} <span>({selected.instrument.ticker})</span></h3>
              <span>{selected.instrument.country} / {selected.instrument.exchange} / {selected.quote.source} / {selected.quote.freshness}</span>
            </div>
            <div className="detail-stats monitor-detail-stats">
              <MetricPill label={`${range} High`} value={`${formatPrice(selected.rangeHigh, selected.instrument.assetClass)} / ${selected.rangeHighDate || "n/a"}`} tone="good" />
              <MetricPill label={`${range} Low`} value={`${formatPrice(selected.rangeLow, selected.instrument.assetClass)} / ${selected.rangeLowDate || "n/a"}`} tone="bad" />
              <MetricPill label="Range Position" value={`${selected.rangePositionPct.toFixed(0)} percentile`} />
              <MetricPill label="Coverage" value={`${selected.coverage.coveragePct.toFixed(0)}% / ${selected.coverage.points} bars`} tone={selected.coverage.coveragePct >= 95 ? "good" : "warn"} />
            </div>
          </div>
          <DeferredMonitoringChart
            loading={detailLoading}
            history={detailHistory}
            window={range}
            high={selected.rangeHigh}
            low={selected.rangeLow}
            highDate={selected.rangeHighDate}
            lowDate={selected.rangeLowDate}
          />
          <div className="monitor-detail-foot">
            <span>Source: <b>{selected.quote.source}</b></span>
            <span>License: <b>{selected.licenseStatus}</b></span>
            <span>As of: <b>{formatTimestamp(selected.quote.asOf)}</b></span>
            <span>Quality: <b>{selected.qualityFlags.length ? selected.qualityFlags.join(", ") : "No flags"}</b></span>
          </div>
        </div>
      )}

    </section>
  );
}

const MarketCard = memo(function MarketCard({ metric, range, selected, onSelect }: {
  metric: MonitoringMetric;
  range: MonitoringWindow;
  selected: boolean;
  onSelect: (instrumentId: string) => void;
}) {
  const hasData = metric.coverage.points > 0 && metric.quote.price > 0;
  const danger = hasData && metric.instrument.assetClass !== "rate" && metric.drawdownFromRangeHighPct <= -15;
  const position = Math.max(0, Math.min(100, metric.rangePositionPct));
  return (
    <button className={`market-card ${selected ? "is-active" : ""}`} onClick={() => onSelect(metric.instrument.id)} aria-pressed={selected}>
      <div className="market-card-top">
        <span>{assetClassLabel(metric.instrument.assetClass)} / {metric.instrument.country}</span>
        <b>{metric.instrument.name}</b>
        <small>{metric.instrument.ticker} / {metric.quote.source}</small>
      </div>
      <div className={`freshness ${metric.quote.freshness}`}>{metric.quote.freshness}</div>
      <Sparkline history={metric.history} danger={danger} assetClass={metric.instrument.assetClass} range={range} />
      <div className="market-card-values">
        <strong className={hasData ? "" : "no-data"}>{hasData ? formatPrice(metric.quote.price, metric.instrument.assetClass) : "No data"}</strong>
        <span className={hasData ? (metric.quote.changePct < 0 ? "red" : "green") : "amber"}>{hasData ? formatChange(metric) : "refresh"}</span>
      </div>
      <div className="range-meter" aria-label={hasData ? `${range} range position ${position.toFixed(0)} percent` : `${range} range unavailable`}>
        <span style={{ width: hasData ? `${Math.max(2, position)}%` : "0" }} />
      </div>
      <div className="market-card-foot">
        {hasData ? <>
          <span className={danger ? "red" : "amber"}>{formatHighDistance(metric)} from {range} high</span>
          <span className="green">{formatLowDistance(metric)} from {range} low</span>
        </> : <>
          <span className="amber">No stored {range} history</span>
          <span>Manual refresh required</span>
        </>}
      </div>
      <div className="market-card-meta">
        <span>{hasData ? `${position.toFixed(0)}th pct` : "0 bars"}</span>
        <span>{metric.coverage.coveragePct.toFixed(0)}% coverage</span>
      </div>
    </button>
  );
});

function DeferredMonitoringChart({ loading, history, window, high, low, highDate, lowDate }: {
  loading: boolean;
  history: DailyBar[];
  window: MonitoringWindow;
  high: number;
  low: number;
  highDate: string;
  lowDate: string;
}) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const element = shellRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setActive(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setActive(entry?.isIntersecting ?? false);
    }, { rootMargin: "300px 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={shellRef} className={loading ? "monitor-chart-shell is-loading" : "monitor-chart-shell"}>
      {active ? (
        <Suspense fallback={<div className="monitor-chart-skeleton"><span /></div>}>
          <MonitoringHistoryChart
            history={history}
            window={window}
            high={high}
            low={low}
            highDate={highDate}
            lowDate={lowDate}
          />
        </Suspense>
      ) : <div className="monitor-chart-skeleton is-deferred"><span /></div>}
    </div>
  );
}

function MarketCardSkeleton() {
  return <div className="market-card market-card-skeleton"><span /><b /><i /><em /></div>;
}

function MetricPill({ label, value, tone }: { label: string; value: string; tone?: "good" | "warn" | "bad" }) {
  return <div className={`metric-pill ${tone ?? ""}`}><span>{label}</span><b>{value}</b></div>;
}

function Ranking({ title, data, value, dailyMove = false }: { title: string; data: MonitoringMetric[]; value: (item: MonitoringMetric) => string; dailyMove?: boolean }) {
  return (
    <div className="side-panel">
      <div className="section-title">{title}</div>
      {data.map((item, index) => (
        <div className="rank-row" key={item.instrument.id}>
          <span><i>{index + 1}</i>{item.instrument.name}</span>
          <b>{item.instrument.ticker}</b>
          <em className={dailyMove && item.quote.changePct >= 0 ? "green" : "red"}>{value(item)}</em>
        </div>
      ))}
      {!data.length && <p className="notes">No stored observations in this view.</p>}
    </div>
  );
}

function QualityRow({ label, value, tone }: { label: string; value: string; tone?: "good" | "warn" | "bad" }) {
  return <div className="quality-row"><span>{label}</span><b className={tone ?? ""}>{value}</b></div>;
}

function formatPrice(value: number, assetClass: AssetClass) {
  if (!Number.isFinite(value)) return "n/a";
  if (assetClass === "rate") return `${number(value, 3)}%`;
  const digits = assetClass === "fx" ? (value < 10 ? 4 : 2) : value < 10 ? 3 : value < 1000 ? 2 : 0;
  return number(value, digits);
}

function formatChange(metric: MonitoringMetric) {
  if (metric.instrument.assetClass === "rate") return `${metric.quote.changePct >= 0 ? "+" : ""}${metric.quote.changePct.toFixed(1)} bp`;
  return pct(metric.quote.changePct);
}

function formatHighDistance(metric: MonitoringMetric) {
  if (metric.instrument.assetClass === "rate") return `${((metric.quote.price - metric.rangeHigh) * 100).toFixed(0)} bp`;
  return pct(metric.drawdownFromRangeHighPct);
}

function formatLowDistance(metric: MonitoringMetric) {
  if (metric.instrument.assetClass === "rate") {
    const value = (metric.quote.price - metric.rangeLow) * 100;
    return `${value >= 0 ? "+" : ""}${value.toFixed(0)} bp`;
  }
  return pct(metric.reboundFromRangeLowPct);
}

function formatDrawdown(metric: MonitoringMetric) {
  return metric.instrument.assetClass === "rate" ? formatHighDistance(metric) : pct(metric.drawdownFromRangeHighPct);
}

function isTerminalRun(run: RefreshRunRecord) {
  return run.status === "completed" || run.status === "partial" || run.status === "failed";
}

function formatRefreshStatus(run: RefreshRunRecord) {
  if (run.status === "completed") return `Updated ${run.succeeded} of ${run.total} instruments`;
  if (run.status === "partial") return `Updated ${run.succeeded}; ${run.failed} feeds kept their cached data`;
  if (run.status === "failed") return run.lastError ?? "Refresh failed";
  return `Updating ${run.completed} of ${run.total} instruments${run.currentInstrumentId ? ` / ${run.currentInstrumentId}` : ""}`;
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}

function formatTimestamp(value: string) {
  if (!value) return "No stored update";
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return value.replace("T", " ").slice(0, 19);
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Seoul"
  }).format(timestamp);
}

function selectMetricsForTab(universe: MonitoringMetric[], tab: MonitorTab) {
  if (tab === "overview") {
    return universe
      .filter((metric) => metric.instrument.overviewOrder !== undefined)
      .sort((left, right) => (left.instrument.overviewOrder ?? 999) - (right.instrument.overviewOrder ?? 999));
  }
  return universe.filter((metric) => metric.instrument.assetClass === tab);
}

function assetClassLabel(assetClass: AssetClass) {
  if (assetClass === "fx") return "FX";
  if (assetClass === "rate") return "Interest Rate";
  return assetClass.charAt(0).toUpperCase() + assetClass.slice(1);
}

function readUrlState(): { tab: MonitorTab; range: MonitoringWindow; instrumentId: string } {
  const params = new URLSearchParams(window.location.search);
  const tab = tabs.some((item) => item.id === params.get("monitorTab")) ? params.get("monitorTab") as MonitorTab : "overview";
  const range = ranges.includes(params.get("monitorRange") as MonitoringWindow) ? params.get("monitorRange") as MonitoringWindow : "5Y";
  return { tab, range, instrumentId: params.get("instrument") ?? "spx" };
}

function writeUrlState(tab: MonitorTab, range: MonitoringWindow, instrumentId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set("monitorTab", tab);
  url.searchParams.set("monitorRange", range);
  if (instrumentId) url.searchParams.set("instrument", instrumentId);
  window.history.replaceState(null, "", url);
}
